import React, { useState } from 'react';
import { forgotPassword, terminateAccount } from '../../../modules/users';
import { useNotification } from '../../../hooks/NotificationContext';
import TableActionsMenu from '../../elements/TableActionsMenu';
import ConfirmationModal from '../../elements/ConfirmationModal';
import { resendLinkToUser } from '../../../lib/utils';
import { modifyCustomerStatus, upgradeToFreemium } from '../../../modules/admin';
import { createExportRequest } from '../../../modules/project';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { FileDownload, InfoOutlined } from '@mui/icons-material';

const CustomerTableActions = ({ row, fetchCustomerList, latestExport = null, updateExportRequest }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addNotification } = useNotification();
  const { organization_id: organizationId, customer } = row;

  const user = row?.org_admin || {};
  const { status, platform, full_name, email, id } = user;

  const handleOpenConfirm = (action) => {
    setConfirmAction(action);
    setShowConfirm(true);
  };

  const handleCloseConfirm = () => setShowConfirm(false);

  const handleExportOrganization = async () => {
    const payload = {
      projects: 'all',
      organizationId: organizationId,
      options: ['testcases'],
      existingRequest: null,
      exportType: 'new'
    };

    try {
      await createExportRequest(payload);
      if (updateExportRequest) {
        await updateExportRequest();
      }
      addNotification('success', 'Export request created successfully. You will receive an email when the export is ready.');
    } catch (error) {
      addNotification('error', error?.message || 'Failed to create export request');
    }
  };

  const handleDownloadExport = () => {
    if (latestExport && latestExport.status === 'completed' && latestExport.export_link) {
      const link = document.createElement('a');
      link.href = latestExport.export_link;
      link.download = latestExport.export_key || 'export.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      addNotification('success', 'Download started');
    } else {
      addNotification('error', 'Export file not available');
    }
  };

  const handleSubmit = async () => {
    if (!confirmAction) return;

    setLoading(true);
    try {
      let response;

      if (confirmAction === 'resetPassword') {
        await forgotPassword({ email });
        addNotification('success', 'Password reset link sent successfully!');
      } else if (confirmAction === 'resendConfirmationEmail') {
        await handleResendLink(email);
      } else if (confirmAction === 'exportCustomerData') {
        await handleExportOrganization();
      } else if (confirmAction === 'upgradeToFreemium') {
        response = await upgradeToFreemium(organizationId);
        fetchCustomerList();
        addNotification('success', response?.message || 'Organization upgraded to Freemium successfully!');
      } else if (confirmAction === 'permanentDeleteCustomer') {
        response = await terminateAccount(organizationId);
        fetchCustomerList();
        addNotification(
          'success',
          response?.message || 'Customer permanently deleted successfully!'
        );
      } else {
        response = await modifyCustomerStatus({
          organizationId,
          type: confirmAction,
        });
        fetchCustomerList();
        addNotification(
          'success',
          response?.message || 'Customer updated successfully!'
        );
      }

    } catch (err) {
      addNotification('danger', err.message);
    } finally {
      setLoading(false);
      handleCloseConfirm();
    }
  };

  const hasRequestedExport = latestExport &&
    (latestExport.status === 'requested' || latestExport.status === 'processing');

  const hasCompletedExport = latestExport &&
    latestExport.status === 'completed' &&
    latestExport.export_link;

  const resetPasswordConfirmationBody =
    platform === 'web'
      ? `Are you sure you want to reset the password for ${full_name}? 
        A link will be sent to their registered email to reset their password.`
      : `This user signed up using an external authentication method and cannot reset their password manually.`;

  const exportConfirmationBody = (
    <>
      <Typography variant="body2" gutterBottom>
        Are you sure you want to export all data for <strong>{customer}</strong>?
      </Typography>

      <Typography variant="body2" sx={{ mt: 1 }}>
        This export will include:
      </Typography>

      <ul style={{ marginTop: 6, marginBottom: 6, paddingLeft: 18 }}>
        <li>All projects in this organization</li>
        <li>All test cases, including test steps and expected results</li>
        <li>All related data associated with these projects</li>
      </ul>

      <Typography variant="body2">
        This process may take some time to complete. We will notify you once the export is ready. The exported data will be available for download for 7 days.
      </Typography>
    </>
  );

  const menuOptionsConfig = {
    resetPassword: {
      label: 'Reset Admin Password',
      visible: status === 'active',
      action: () => handleOpenConfirm('resetPassword'),
    },
    deactivate: {
      label: 'Deactivate Customer',
      visible: status === 'active' || status === 'pending',
      action: () => handleOpenConfirm('deactivate'),
    },
    activate: {
      label: 'Activate Customer',
      visible: status === 'inactive' || status === 'suspended',
      action: () => handleOpenConfirm('activate'),
    },
    upgradeToFreemium: {
      label: 'Upgrade to Freemium',
      visible: status === 'active' && row.type === 'Free',
      action: () => handleOpenConfirm('upgradeToFreemium'),
    },
    exportCustomerData: {
      label: hasRequestedExport ? 'Export In Progress...' : 'Export Customer Data',
      visible: status !== 'pending',
      action: () => handleOpenConfirm('exportCustomerData'),
      disabled: !!hasRequestedExport,
    },
    manageSubscription: {
      label: 'Manage Subscription',
      visible: status !== 'delete-requested',
      action: () => (window.location.href = `/subscriptions/${id}`),
      disabled: true,
    },
    cancelDeletion: {
      label: 'Cancel Deletion',
      visible: status === 'delete-requested',
      action: () => handleOpenConfirm('cancelDeletion'),
    },
    resendConfirmationEmail: {
      label: 'Resend Confirmation Email',
      visible: status === 'pending',
      action: () => handleOpenConfirm('resendConfirmationEmail'),
    },
    permanentDeleteCustomer: {
      label: 'Permanently Delete Customer',
      visible: status === 'inactive',
      action: () => handleOpenConfirm('permanentDeleteCustomer'),
    },
  };

  const menuOptions = Object.values(menuOptionsConfig)
    .filter((option) => option.visible)
    .map(({ label, action, disabled }) => ({
      label,
      icon: '',
      onClick: action,
      disabled,
    }));

  const confirmationMessages = {
    resetPassword: resetPasswordConfirmationBody,
    activate: `Are you sure you want to activate ${customer}? This will allow the organization's users to access the platform again.`,
    deactivate: `Are you sure you want to deactivate ${customer}? This will prevent the organization's users from accessing the platform. You can reactivate them at any time.`,
    upgradeToFreemium: `Are you sure you want to upgrade ${customer} to Freemium? This will allow the organization to add multiple projects with multiple users.`,
    cancelDeletion: `Are you sure you want to cancel the account deletion request for ${customer}? This will re-activate the customer and allow them to access the platform again?`,
    resendConfirmationEmail: `The org admin has not verified their email. Are you sure you want to resend the confirmation email to ${full_name}?`,
    exportCustomerData: exportConfirmationBody,
    permanentDeleteCustomer: `Are you absolutely sure you want to permanently delete "${customer}"? 
    This action cannot be undone. All organization data, projects, users, and related records will be permanently removed from the system.`,
  };

  const handleResendLink = async (userEmail) => {
    try {
      const response = await resendLinkToUser(userEmail, 'confirmation');
      addNotification('success', response);
    } catch (error) {
      addNotification('error', error.message);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

      <TableActionsMenu options={menuOptions} />
        {hasRequestedExport && (
          <Tooltip title="Request received. Preparing export." arrow>
            <InfoOutlined sx={{ color: '#1976d2', fontSize: '20px' }} />
          </Tooltip>
        )}
        {hasCompletedExport && (
          <Tooltip title="Your export is ready. Available for download for 7 days." arrow>
            <IconButton
              size="small"
              onClick={handleDownloadExport}
              sx={{
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)'
                }
              }}
            >
              <FileDownload sx={{ fontSize: '20px' }} />
            </IconButton>
          </Tooltip>
        )}

      <ConfirmationModal
        show={showConfirm}
        handleClose={handleCloseConfirm}
        handleConfirm={handleSubmit}
        title={
          confirmAction
            ? confirmAction
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, str => str.toUpperCase())
              .trim()
            : ''
        }
        body={confirmationMessages[confirmAction] || ''}
        buttonLabel={loading ? 'Please wait...' : 'Confirm'}
        buttonLabelSecondary="Cancel"
        buttonStyle={confirmAction === 'permanentDeleteCustomer' ? 'error' : 'primary'}
        loading={loading}
        hideButton={confirmAction === 'resetPassword' && platform !== 'web'}
      />
    </div>
  );
};

export default CustomerTableActions;
